// Block-of-five shipping. Shipping is charged once per five physical books in
// an order, not per item: 1-5 physical units cost one shipping block, 6-10 cost
// two, and so on. eBooks and audiobooks never ship and never count towards the
// physical unit total.
//
// Keep this file in sync with functions/lib/shopShipping.js: Cloud Functions
// cannot import from src/, so the two implementations must match. The server
// copy (createOrderCheckout) is authoritative; the cart preview here and in
// ReaderPortal mirror it so the price shown matches the price charged.

import { isPhysicalProduct } from './productCategory';

// Zone keyword lists must match getZoneShipping in functions/index.js.
const UK = ['uk', 'gb', 'united kingdom', 'england', 'scotland', 'wales', 'northern ireland', 'great britain'];
const EUROPE = ['austria', 'belgium', 'bulgaria', 'croatia', 'cyprus', 'czech', 'denmark', 'estonia', 'finland',
  'france', 'germany', 'greece', 'hungary', 'iceland', 'ireland', 'italy', 'latvia', 'lithuania', 'luxembourg',
  'malta', 'netherlands', 'norway', 'poland', 'portugal', 'romania', 'slovakia', 'slovenia', 'spain', 'sweden',
  'switzerland', 'turkey', 'ukraine', 'serbia', 'bosnia', 'montenegro', 'albania', 'macedonia', 'moldova'];
const US_CANADA = ['us', 'usa', 'united states', 'america', 'canada', 'ca'];

// Returns 'uk' | 'eu' | 'us_ca' | 'row' for a country string.
export function shippingZone(country) {
  const c = (country || '').trim().toLowerCase();
  if (!c) return 'row';
  if (UK.some((z) => c.includes(z))) return 'uk';
  if (EUROPE.some((z) => c.includes(z))) return 'eu';
  if (US_CANADA.some((z) => c.includes(z))) return 'us_ca';
  return 'row';
}

// The regional shipping rate for a single product in a given zone, with the
// same field fallbacks the server uses.
export function productZoneRate(product, zone) {
  if (!product) return 0;
  if (zone === 'uk') return Number(product.shippingUK ?? product.shippingDomestic ?? 3.50);
  if (zone === 'eu') return Number(product.shippingEurope ?? product.shippingInternational ?? 8.00);
  if (zone === 'us_ca') return Number(product.shippingUSCanada ?? 12.00);
  return Number(product.shippingRestOfWorld ?? product.shippingInternational ?? 14.00);
}

// items: array of { product, quantity }. Returns the block-of-five breakdown.
// ratePerBlock is the HIGHEST physical rate among the products in the cart for
// the destination zone (so a heavier item never under-charges shipping).
export function computeBlockShipping(items, country) {
  const zone = shippingZone(country);
  let physicalUnits = 0;
  let ratePerBlock = 0;
  for (const entry of items || []) {
    const product = entry.product;
    if (!product || !isPhysicalProduct(product)) continue;
    const qty = Math.max(0, parseInt(entry.quantity, 10) || 0);
    if (qty <= 0) continue;
    physicalUnits += qty;
    const rate = productZoneRate(product, zone);
    if (rate > ratePerBlock) ratePerBlock = rate;
  }
  const shippingUnits = Math.ceil(physicalUnits / 5);
  const shippingCost = Math.round(shippingUnits * ratePerBlock * 100) / 100;
  return { zone, physicalUnits, shippingUnits, ratePerBlock, shippingCost };
}
